import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import YouTube from "react-youtube";
import "../styles/VideoPlayer.css";

const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

const VideoPlayer = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMinimized, setIsMinimized] = useState(false);
  const [videoTitle, setVideoTitle] = useState("Loading...");
  const [videoDescription, setVideoDescription] = useState(
    "Fetching video details..."
  );
  const [playlistItems, setPlaylistItems] = useState([]);
  const [isFromPlaylist, setIsFromPlaylist] = useState(false);
  const [videoDuration, setVideoDuration] = useState("0 min");
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedToastVisible, setCopiedToastVisible] = useState(false);

  // Extract playlist ID from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const playlistId = urlParams.get("list");
  const [areControlsVisible, setAreControlsVisible] = useState(true);

  useEffect(() => {
    const minimizedVideo = localStorage.getItem("minimizedVideo");
    if (minimizedVideo) {
      setIsMinimized(true);
    }

    // Fetch video details
    const fetchVideoDetails = async () => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?key=${API_KEY}&part=snippet,contentDetails&id=${videoId}`
        );
        const data = await response.json();

        if (data.items.length > 0) {
          const videoData = data.items[0];
          setVideoTitle(videoData.snippet.title);
          setVideoDescription(videoData.snippet.description);
          setVideoDuration(parseDuration(videoData.contentDetails.duration));
        } else {
          setVideoTitle("Video not found");
          setVideoDescription("The video details could not be retrieved.");
        }
      } catch (error) {
        console.error("Error fetching video details:", error);
      }
    };

    fetchVideoDetails();

    // Fetch playlist items if the video is part of a playlist
    if (playlistId) {
      setIsFromPlaylist(true);
      const fetchPlaylistItems = async () => {
        try {
          const response = await fetch(
            `https://www.googleapis.com/youtube/v3/playlistItems?key=${API_KEY}&part=snippet,contentDetails&playlistId=${playlistId}&maxResults=5`
          );
          const data = await response.json();

          if (data.items) {
            const videoIds = data.items.map(
              (item) => item.contentDetails.videoId
            );

            // Fetch durations separately
            const durationResponse = await fetch(
              `https://www.googleapis.com/youtube/v3/videos?key=${API_KEY}&part=contentDetails&id=${videoIds.join(
                ","
              )}`
            );
            const durationData = await durationResponse.json();

            const formattedPlaylist = data.items.map((item, index) => ({
              id: item.contentDetails.videoId,
              title: item.snippet.title,
              thumbnail: item.snippet.thumbnails.high.url,
              duration: parseDuration(
                durationData.items[index]?.contentDetails?.duration || "PT0M0S"
              ),
            }));

            setPlaylistItems(formattedPlaylist);
          }
        } catch (error) {
          console.error("Error fetching playlist videos:", error);
        }
      };

      fetchPlaylistItems();
    } else {
      setIsFromPlaylist(false);
      setPlaylistItems([]);
    }
  }, [videoId, playlistId]);

  // Convert ISO duration (PT4M3S) to readable minutes
  const parseDuration = (isoDuration) => {
    const match = isoDuration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);

    const hours = match[1] ? parseInt(match[1].replace("H", "")) : 0;
    const minutes = match[2] ? parseInt(match[2].replace("M", "")) : 0;
    const seconds = match[3] ? parseInt(match[3].replace("S", "")) : 0;

    const totalMinutes = Math.round(hours * 60 + minutes + seconds / 60); // Convert everything to minutes
    return `${totalMinutes} min`;
  };

  // Close video and return to academy
  const handleClose = () => {
    localStorage.removeItem("minimizedVideo");
    navigate("/academy");
  };

  // Minimize video and return to academy
  const handleMinimize = () => {
    const minimizedData = {
      videoId,
      title: videoTitle,
    };

    localStorage.setItem("minimizedVideo", JSON.stringify(minimizedData));
    navigate("/academy");
  };

  // Handle clicking a playlist video
  const handlePlaylistVideoClick = (nextVideoId) => {
    navigate(`/watch/${nextVideoId}?list=${playlistId}`);
    window.location.reload();
  };

  useEffect(() => {
    // Hide '.footer-content-2' when '.video-details' is present
    const footer = document.querySelector(".footer-content-2");
    if (footer) {
      footer.style.display = "none";
    }

    return () => {
      // Show '.footer-content-2' when leaving the video screen
      if (footer) {
        footer.style.display = "block";
      }
    };
  }, []);

  useEffect(() => {
    // Hide the .mobile-navbar when this component is mounted
    const navbar = document.querySelector("nav.mobile-navbar");
    if (navbar) {
      navbar.style.display = "none";
    }

    // When the component unmounts, show the .mobile-navbar again
    return () => {
      if (navbar) {
        navbar.style.display = "";
      }
    };
  }, []);

  useEffect(() => {
    // Always scroll to top when this component (video player) is loaded
    window.scrollTo(0, 0);
  }, [videoId]);

  useEffect(() => {
    let hideTimeout;

    // This function will be called whenever the user moves the mouse
    const handleMouseMove = () => {
      // Show controls
      setAreControlsVisible(true);

      // Clear any old timeout and start a new 3s countdown
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        setAreControlsVisible(false);
      }, 3000); // 3 seconds of inactivity -> hide controls
    };

    // We attach the event to whichever container wraps the YouTube player
    // In your code, that might be .video-widget or .video-widget-desktop
    const container =
      document.querySelector(".video-widget-desktop") ||
      document.querySelector(".video-widget");

    if (container) {
      container.addEventListener("mousemove", handleMouseMove);
    }

    // Cleanup
    return () => {
      if (container) {
        container.removeEventListener("mousemove", handleMouseMove);
      }
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, []);

  const shareLink = `https://www.youtube.com/watch?v=${videoId}`;

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      // Show toast instead of an alert
      setCopiedToastVisible(true);
      setTimeout(() => {
        setCopiedToastVisible(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  // For WhatsApp share
  const handleShareWhatsApp = () => {
    // This uses the WhatsApp URI scheme
    const whatsappUrl = `https://wa.me/?text=Check this out: ${shareLink}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShareTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=Check this out: ${shareLink}`,
      "_blank"
    );
  };

  const renderMobile = () => (
    <div className={`video-player-container ${isMinimized ? "minimized" : ""}`}>
      {/* Video Widget */}
      <div className="video-widget">
        <YouTube
          videoId={videoId}
          opts={{ width: "100%", height: isMinimized ? "120" : "360" }}
        />
        {areControlsVisible && (
          <div className="controls">
            <button className="minimize-btn" onClick={handleMinimize}>
              <img src="/static/images/minimizeSymbol.png" alt="Minimize" />
            </button>
            <button className="close-btn" onClick={handleClose}>
              <img src="/static/images/closeSymbol.png" alt="Close" />
            </button>
          </div>
        )}
      </div>

      {/* Video Details */}
      {!isMinimized && (
        <div className="video-details">
          <h2>{videoTitle}</h2>
          <p>{videoDuration}</p>
          <button className="share-btn" onClick={handleShareClick}>
            <img
              src="/static/images/shareSymbol.png"
              alt="Share"
              className="share-symbol"
            />
            Share
          </button>
          <p>{videoDescription}</p>
          {/* Playlist "Up Next" Section */}
          {isFromPlaylist && playlistItems.length > 0 && (
            <div className="up-next-section">
              <h3>Up Next</h3>
              {playlistItems.map((video) => (
                <div
                  key={video.id}
                  className="playlist-video"
                  onClick={() => handlePlaylistVideoClick(video.id)}
                >
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="playlist-thumbnail"
                  />
                  <div className="playlist-video-details">
                    <h4>{video.title}</h4>
                    <p>{video.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* --- SHARE MODAL --- */}
          {showShareModal && (
            <div className="share-modal-overlay">
              <div className="share-modal-content">
                <h3>Share on social media</h3>
                <p className="link-to-share">Link to share</p>

                {/* Link box with copy icon */}
                <div className="share-link-box">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="share-link-input"
                  />
                  <button className="copy-button" onClick={handleCopyLink}>
                    <i className="fa fa-clone"></i>
                  </button>
                </div>

                {/* Social icons */}
                <div className="social-icons">
                  <button onClick={handleShareWhatsApp}>
                    <i className="fa-brands fa-whatsapp"></i>
                  </button>
                  <button onClick={handleShareTwitter}>
                    <i class="fa-brands fa-x-twitter"></i>{" "}
                  </button>
                </div>

                <button
                  className="close-share-modal"
                  onClick={handleCloseShareModal}
                >
                  Close
                </button>
              </div>
            </div>
          )}
          {copiedToastVisible && (
            <div className="copied-toast">Link copied!</div>
          )}
        </div>
      )}
    </div>
  );

  const renderDesktop = () => (
    <div
      className={`video-player-container-desktop ${
        isMinimized ? "minimized" : ""
      }`}
    >
      <div className="video-widget-desktop">
        <YouTube
          videoId={videoId}
          opts={{ width: "100%", height: isMinimized ? "120" : "480" }}
        />
        {areControlsVisible && (
          <div
            className={`controls-desktop ${
              areControlsVisible ? "fade-in-controls" : "fade-out-controls"
            }`}
          >
            <button className="minimize-btn" onClick={handleMinimize}>
              <img src="/static/images/minimizeSymbol.png" alt="Minimize" />
            </button>
            <button className="close-btn" onClick={handleClose}>
              <img src="/static/images/closeSymbol.png" alt="Close" />
            </button>
          </div>
        )}
      </div>
      {!isMinimized && (
        <div className="video-details">
          <h2>{videoTitle}</h2>
          <p>{videoDuration}</p>
          <button className="share-btn" onClick={handleShareClick}>
            <img
              src="/static/images/shareSymbol.png"
              alt="Share"
              className="share-symbol"
            />
            Share
          </button>
          <p>{videoDescription}</p>
          {isFromPlaylist && playlistItems.length > 0 && (
            <div className="up-next-section">
              <h3>Up Next</h3>
              {playlistItems.map((video) => (
                <div
                  key={video.id}
                  className="playlist-video"
                  onClick={() => handlePlaylistVideoClick(video.id)}
                >
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="playlist-thumbnail"
                  />
                  <div className="playlist-video-details">
                    <h4>{video.title}</h4>
                    <p>{video.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- SHARE MODAL --- */}
      {showShareModal && (
        <div className="share-modal-overlay">
          <div className="share-modal-content">
            <h3>Share on social media</h3>
            <p className="link-to-share">Link to share</p>

            {/* Link box with copy icon */}
            <div className="share-link-box">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="share-link-input"
              />
              <button className="copy-button" onClick={handleCopyLink}>
                <i className="fa fa-clone"></i>
              </button>
            </div>

            {/* Social icons */}
            <div className="social-icons">
              <button onClick={handleShareWhatsApp}>
                <i className="fa-brands fa-whatsapp"></i>
              </button>
              <button onClick={handleShareTwitter}>
                <i class="fa-brands fa-x-twitter"></i>{" "}
              </button>
            </div>

            <button
              className="close-share-modal"
              onClick={handleCloseShareModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {copiedToastVisible && <div className="copied-toast">Link copied!</div>}
    </div>
  );

  return isMobile ? renderMobile() : renderDesktop();
};

export default VideoPlayer;
